<template>
  <div class="position-absolute w-100" id="login-page">
    <div id="image-filter"></div>
    <div id="white-rectangle"></div>
    <b-container fluid class="h-100 d-flex justify-content-center px-0">
      <b-row align-v="center" align-h="center" class="w-100">
        <b-col
          sm="12"
          md="7"
          lg="6"
          xl="3"
          id="input-box-login"
          class="bg-white p-0 shadow-sm"
        >
          <!-- Logo -->
          <b-row
            :no-gutters="true"
            align-h="center"
            id="logo-section"
            class="pt-4"
          >
            <img src="@/../public/img/logo.jpg" />
          </b-row>
          <div id="inputs-section">
            <form v-on:submit.prevent="">
              <!-- Champs texte -->
              <div class="p-0" id="text-inputs">
                <!-- Courriel -->
                <InputTextEmail
                  identifier="email"
                  :label="'components.inputs.inputEmail.label' | i18n"
                  :initialValue="email"
                  @onInput="
                    data => {
                      email = data.value;
                      isEmailValid = data.validation.isValid;
                    }
                  "
                  @onChange="
                    data => {
                      email = data.value;
                      isEmailValid = data.validation.isValid;
                    }
                  "
                />

                <!-- Mot de passe -->
                <InputTextPassword
                  identifier="password"
                  :label="'components.inputs.inputPassword.label' | i18n"
                  :initialValue="password"
                  @onInput="
                    data => {
                      password = data.value;
                      isPasswordValid = data.validation.isValid;
                    }
                  "
                  @onChange="
                    data => {
                      password = data.value;
                      isPasswordValid = data.validation.isValid;
                    }
                  "
                />
              </div>

              <b-row no-gutters align-h="between" align-v="center">
                <!-- Option « Se souvenir de moi » -->
                <InputCheckbox
                  :message="'login.rememberme' | i18n"
                  @update-status="
                    status => {
                      rememberMe = status;
                    }
                  "
                />
                <!-- Lien « Besoin d'aide » -->
                <b-link
                  class="font-weight-bold"
                  href="mailto:info@logicielradar.com"
                  >{{ "login.needHelp" | i18n }}</b-link
                >
              </b-row>

              <!-- Bouton -->
              <b-row :no-gutters="true" align-h="around">
                <button
                  :disabled="isSubmitting"
                  id="button-login"
                  class="mt-5 d-flex align-items-center justify-content-center rounded-circle btn"
                  :class="isSubmitting ? 'btn-light' : 'btn-primary'"
                  @click="submitButtonClicked"
                >
                  <b-spinner
                    v-if="isSubmitting"
                    id="spinner"
                    variant="dark"
                    label="Spinning"
                    small
                  ></b-spinner>
                  <i v-else class="check icon m-0 mb-1"></i>
                </button>
              </b-row>
            </form>
          </div>
        </b-col>
      </b-row>

      <!-- Erreur -->
      <b-toast
        id="error-toast"
        toast-class="bg-danger text-white text-center"
        :no-close-button="true"
      >
        <template v-slot:default>
          <i class="exclamation circle icon mr-3"></i>
          <span>{{ error }}</span>
        </template>
      </b-toast>
    </b-container>
  </div>
</template>

<script>
import { InputTextEmail, InputTextPassword, InputCheckbox } from "@/components";
/**
 * @description Page de connexion
 * @author Alexandre Gallant
 * @vue-data {String} email - La valeur du courriel
 * @vue-data {String} password - La valeur du mot de passe
 * @vue-data {Boolean} rememberMe - Si l'on doit « se souvenir » de l'utilisateur
 * @vue-data {Boolean} isEmailValid - Si la valeur du courriel est valide
 * @vue-data {Boolean} isPasswordValid - Si la valeur du mot de passe est valide
 * @vue-data {String} error - Le message d'erreur à afficher
 * @vue-data {Boolean} isSubmitting - Si une requête est en cours
 * @vue-computed {Boolean} computedIsDataValid - Si le contenu des contrôles est valide
 * @vue-computed {Function} computedKeyDownEventListener - Fonction à appeler lorsque l'utilisateur appuie sur une touche
 */
export default {
  name: "Login",
  components: {
    InputTextEmail,
    InputTextPassword,
    InputCheckbox
  },
  data: function() {
    return {
      email: "",
      password: "",
      rememberMe: false,
      isEmailValid: false,
      isPasswordValid: false,
      error: "",
      isSubmitting: false
    };
  },
  computed: {
    computedIsDataValid() {
      return this.isEmailValid && this.isPasswordValid;
    },
    computedKeyDownEventListener() {
      return event => {
        if (event.code == "Enter") {
          this.submitButtonClicked();
        }
      };
    }
  },
  methods: {
    /**
     * @description Envoie la requête de connexion à l'API si la valeur des contrôles est valide sinon affiche
     * le message d'erreur indiquant que les valeurs sont invalides
     */
    submitButtonClicked() {
      if (this.computedIsDataValid) {
        this.submitLoginCredidentials(
          this.email,
          this.password,
          this.rememberMe
        );
      } else {
        this.showError(this.$i18n.t("errors.invalidCredentials"));
      }
    },
    /**
     * @description Envoie la requête de connexion à l'API
     * @param {String} email Valeur à envoyer à l'API pour le courriel
     * @param {String} password Valeur à envoyer à l'API pour le mot de passe
     * @param {Boolean} rememberMe Si l'on doit se « souvenir » de l'utilisateur
     */
    submitLoginCredidentials(email, password, rememberMe) {
      this.isSubmitting = true;
      this.$api
        .login(email, password)
        .then(async result => {
          this.storeSubmitResult(result.data, rememberMe);
          const userInfos = await this.getUserInfo();
          this.storeUserInfos(userInfos);
        })
        .catch(() => {
          this.showError(this.$i18n.t("errors.invalidCredentials"));
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    /**
     * @description Récupère et retourne les informations relatives à l'utilisateur connecté
     * @returns {Object} Le résultat de la requête
     */
    async getUserInfo() {
      return (await this.$api.getUserInfo()).data;
    },
    /**
     * @description Enregistre les informations utiles sur l'utilisateur connecté dans « session storage »
     * @param {Object} userInfos Les informations sur l'utilisateur connecté
     */
    storeUserInfos(userInfos) {
      // Note: il semble qu'on veut client_id et non id, sinon on fait face à des "Unauthorized"
      this.$user.set({
        role: userInfos.isSuperAdmin == 1 ? "admin" : "user",
        id: userInfos.id,
        client_id: userInfos.client_id
      });
    },
    /**
     * @description Enregistre l'« access_token » et le « refresh_token » fournis selon si l'on doit se « souvenir » de l'utilisateur ou non
     * @param {Object} result Le résultat de la requête
     * @param {Boolean} rememberMe Si l'on doit se « souvenir » de l'utilisateur
     */
    storeSubmitResult(result, rememberMe) {
      this.$api.axios.defaults.headers.common["Authorization"] =
        "Bearer " + result.access_token;

      if (rememberMe) {
        localStorage.setItem("refresh_token", result.refresh_token);
      } else {
        sessionStorage.setItem("refresh_token", result.refresh_token);
      }
    },
    /**
     * @description Affiche ou cache le message d'erreur selon le paramètre fourni
     * @param {Boolean} hide Si l'on doit cacher le message d'erreur (celui-ci sera affiché dans le cas contraire)
     */
    showError(error) {
      this.error = error;
      this.$bvToast.show("error-toast");
    }
  },
  /**
   * @description Crée un event listener pour « submit » quand l'utilisateur appuie sur la touche « entrer »
   */
  mounted() {
    window.addEventListener("keydown", this.computedKeyDownEventListener);
  },
  /**
   * @description Détruit l'event listener
   */
  beforeDestroy() {
    window.removeEventListener("keydown", this.computedKeyDownEventListener);
  }
};
</script>

<style scoped lang="scss">
a:hover {
  color: $primary;
}

#input-box-login {
  border-radius: 1em;
  z-index: 3;
}

#login-page {
  background-image: url("../../public/img/login-background.jpg");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-color: white;
  height: 100%;
}

#image-filter {
  background-color: $white;
  opacity: 50%;
  width: 100%;
  height: 50%;
  position: absolute;
  top: 0;
  left: 0;
}

#white-rectangle {
  position: absolute;
  top: 50%;
  height: 50%;
  width: 100%;
  background-color: $dark-white;
}

@media screen and (max-width: 600px) {
  #login-page {
    padding: 0;
  }
}

#logo-section {
  margin-top: 37px;
  padding-top: 0 !important;
}

#inputs-section {
  margin: 45px 60px 19px 60px;
}

#text-inputs {
  margin-bottom: 26px;
}

#button-login {
  height: 42px;
  width: 42px;
}
</style>
